/**
 * "Seen" state for notification badges (Sep 2026).
 *
 * A to-do stays a to-do until it is actioned, but its COUNT BADGE should not
 * keep nagging once the user has looked at it. So:
 *   - the bell's badge counts only to-dos the user hasn't seen yet; closing the
 *     bell marks everything in it as seen (the list itself still shows them);
 *   - a sidebar badge hides once the user has opened the place its items live
 *     (the tool's Team/Admin queue), and comes back only when its content
 *     changes (signature = the items' ids AND labels, so a resubmitted item with
 *     the same id but new text badges again).
 *
 * Per browser AND per signed-in user (localStorage key suffixed with the email,
 * set by Layout via setSeenUser), so a shared machine or impersonation
 * never carries one person's seen state to another.
 */
import { createElement, useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_KEY = 'daaily.seen.v2';
/** Oldest keys are dropped past this, so the store can't grow without bound. */
const MAX_KEYS = 600;

let user = '';
const storageKey = () => `${BASE_KEY}:${user}`;

function load(): string[] {
  if (!user) return [];
  try {
    const raw = localStorage.getItem(storageKey());
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

let keys: string[] = [];
let set = new Set<string>();
const listeners = new Set<() => void>();
function emit() { for (const l of listeners) l(); }

/** Called by Layout with the signed-in (possibly impersonated) email. */
export function setSeenUser(email: string) {
  const next = email.toLowerCase();
  if (next === user) return;
  user = next;
  keys = load();
  set = new Set(keys);
  // Called during Layout's render: components rendering after this read the
  // new set directly; already-mounted ones are told after the render pass.
  queueMicrotask(emit);
}

export function markSeen(newKeys: string[]) {
  if (!user) return;
  const fresh = newKeys.filter((k) => !set.has(k));
  if (fresh.length === 0) return;
  keys = [...keys, ...fresh].slice(-MAX_KEYS);
  set = new Set(keys);
  try { localStorage.setItem(storageKey(), JSON.stringify(keys)); } catch { /* private mode: in-memory only */ }
  emit();
}

function subscribe(l: () => void) {
  listeners.add(l);
  // Another tab marked something seen.
  const onStorage = (e: StorageEvent) => {
    if (e.key !== storageKey()) return;
    keys = load();
    set = new Set(keys);
    l();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
}

/** The current seen-set; re-renders the caller whenever it changes. */
export function useSeenSet(): Set<string> {
  return useSyncExternalStore(subscribe, () => set);
}

/** Short stable hash, so content signatures stay small in storage. */
export function hashSig(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** A to-do's seen key: its id plus its label, so changed content counts as new. */
export const todoSeenKey = (t: { id: string; label: string }) => `todo:${t.id}:${hashSig(t.label)}`;

/** Signature for a set of to-dos behind a badge (order-independent). */
export const todosSig = (items: { id: string; label: string }[]) =>
  hashSig(items.map((t) => `${t.id}|${t.label}`).sort().join('\n'));

const onRouteFn = (pathname: string, route: string) => pathname === route || pathname.startsWith(`${route}/`);

/**
 * Sidebar badge visibility. `badgeKey` identifies the badge + its content; the
 * badge is marked seen when the user is on `markRoute` (where its items live).
 */
export function useNavBadge(badgeKey: string, markRoute: string, count: number): boolean {
  const seen = useSeenSet();
  const { pathname } = useLocation();
  const key = `nav:${badgeKey}`;
  const onRoute = onRouteFn(pathname, markRoute);
  useEffect(() => {
    if (onRoute && count > 0) markSeen([key]);
  }, [onRoute, count, key]);
  return count > 0 && !onRoute && !seen.has(key);
}

/**
 * A count badge (or, in the collapsed rail, a dot) that hides once `markRoute`
 * has been visited — see useNavBadge. Parent and child badges of the same tool
 * share one `badgeKey`, so seeing the queue clears both.
 */
export function SeenBadge({
  badgeKey,
  markRoute,
  count,
  className,
  dot = false,
}: {
  badgeKey: string;
  markRoute: string;
  count: number;
  className: string;
  dot?: boolean;
}) {
  const show = useNavBadge(badgeKey, markRoute, count);
  if (!show) return null;
  return createElement('span', { className, 'aria-label': `${count} new` }, dot ? null : count);
}
